import type { ErrorEvent, StackFrame } from '@sentry/react';

const STACK_OVERFLOW_MESSAGE = 'Maximum call stack size exceeded';

// WebKit appends a period, V8 does not.
const isStackOverflowValue = (value: string | undefined): boolean => !!value?.includes(STACK_OVERFLOW_MESSAGE);

/**
 * Whether a frame points at no script we or a third party serve.
 *
 * Chrome on iOS injects its own scripts into the page (autofill, form handling).
 * They inherit the document URL as their source, so a frame from one names a
 * route — or nothing at all — where a frame from a real script names a file.
 * `/assets/*` is ours; an external origin is a third party's; same-origin
 * without `/assets/` belongs to neither.
 */
const isInjectedFrame = (frame: StackFrame, origin: string | undefined): boolean => {
  const filename = frame.filename;

  if (!filename || filename === 'undefined') {
    return true;
  }
  if (filename.includes('/assets/')) {
    return false;
  }
  // A single leading slash is same-origin by construction; `//host/path` is a
  // protocol-relative URL to somewhere else.
  if (filename.startsWith('/')) {
    return !filename.startsWith('//');
  }

  // Parsed, never prefix-matched: `https://<our-host>.evil/x` starts with our
  // origin without being it, and calling that same-origin would drop the very
  // external-script overflow this check exists to keep. An unparseable filename
  // counts as a real script, which errs toward reporting.
  try {
    return !!origin && new URL(filename).origin === origin;
  } catch {
    return false;
  }
};

/**
 * `RangeError: Maximum call stack size exceeded` raised by a script the browser
 * injected into our page — not our code, unfixable from here, and 900+ events
 * across three issues (CUSTOMER-APP-12Y, -136, -MN), all Chrome on iOS.
 *
 * Narrow on purpose, because a real overflow must still report:
 *  - `onerror` only: anything we capture ourselves is ours
 *  - at least one frame: an overflow with no stack at all could be anything
 *  - every frame off-script: Sentry keeps 50 frames, so deep recursion inside an
 *    external script (Stripe, Dintero, GTM) truncates our entry frames away —
 *    those frames still carry an external filename, so they survive this check
 */
export const isInjectedScriptStackOverflow = (event: ErrorEvent, origin: string | undefined): boolean => {
  const exception = event.exception?.values?.[0];

  if (exception?.type !== 'RangeError' || !isStackOverflowValue(exception.value)) {
    return false;
  }
  if (exception.mechanism?.type !== 'onerror') {
    return false;
  }

  const frames = exception.stacktrace?.frames ?? [];
  return frames.length > 0 && frames.every((frame) => isInjectedFrame(frame, origin));
};

// The extension's cache accessors; every observed value is one of these two
// shapes. `nb-NO_wordsDistributor` and `ac_wordsDistributor` are why the name
// class allows hyphens and underscores.
const EXTENSION_TIMEOUT_ACCESSOR =
  /^Request timeout (?:[A-Za-z0-9_-]+Distributor|DefineExpirationForLanguagePacks)\.getValue$/;

// Its capability probes, which carry no `.getValue` suffix.
const EXTENSION_TIMEOUT_PROBES = new Set([
  'Request timeout isDictateAvailable',
  'Request timeout isMathOcrAvailable',
  'Request timeout isPredictionAvailable',
  'Request timeout getDictionariesByLanguageId'
]);

/**
 * A reading/writing assistive browser extension timing out its own internal
 * requests. It rejects with a bare string, so Sentry has no stack to group on
 * and opens a NEW issue per distinct string -- 25 issues from one session on
 * `app.noddi.co/login`, one user, one second.
 *
 * Takes the raw rejection value (`hint.originalException`), not the event: the
 * SDK's rendered `Non-Error promise rejection captured with value: …` wrapper
 * is its own English prose and can change under us, and matching it would
 * discard any bare-string rejection that happened to read `Request timeout X`.
 * The names below are the extension's own vocabulary, so this stays closed
 * over noise we have seen and fails open for everything else -- a name the
 * extension adds later reports until it is listed here, which is the safe
 * direction for a filter that deletes telemetry.
 */
export const isExtensionRequestTimeoutRejection = (originalException: unknown): boolean =>
  typeof originalException === 'string' &&
  (EXTENSION_TIMEOUT_ACCESSOR.test(originalException) || EXTENSION_TIMEOUT_PROBES.has(originalException));

const JAVA_BRIDGE_GONE = 'Java object is gone';

/**
 * Facebook's Android in-app browser injects `iabjs://` scripts that report to
 * the app through a Java bridge. On `beforeunload` the bridge is already torn
 * down, so its own logger throws `Error invoking postMessage: Java object is
 * gone` into our page (CUSTOMER-APP-15C).
 *
 * Both halves are required: the message alone could come from a real WebView
 * bridge we call, and our bundle never serves an `iabjs://` script.
 */
export const isFacebookInAppBridgeGone = (event: ErrorEvent): boolean => {
  const exception = event.exception?.values?.[0];

  if (!exception?.value?.includes(JAVA_BRIDGE_GONE)) {
    return false;
  }
  return (exception.stacktrace?.frames ?? []).some((frame) => !!frame.filename?.startsWith('iabjs://'));
};
